import { TRPCClientError } from '@trpc/client'

interface ErrorData {
  code?: string
  httpStatus?: number
  reason?: string | null
  fields?: Record<string, string> | null
  issues?: unknown
}

function data(err: unknown): ErrorData | null {
  if (err instanceof TRPCClientError) return (err.data ?? null) as ErrorData | null
  return null
}

/** The server's machine-readable reason (e.g. "locked"), never shown as is. */
export function errorReason(err: unknown): string | null {
  return data(err)?.reason ?? null
}

export function errorCode(err: unknown): string | null {
  return data(err)?.code ?? null
}

/**
 * A form's errors by field, as the server sends them (PRD 00 §4.6): a code for each field it
 * refused («taken», «required»…), never the value that was sent. None when the refusal is not
 * about a field.
 */
export function errorFields(err: unknown): Readonly<Record<string, string>> {
  const fields = data(err)?.fields
  return fields && typeof fields === 'object' ? fields : {}
}

/** A problem of a field of a document, as the server names it: its path and a code. */
export interface ErrorIssue {
  path: (string | number)[]
  code: string
}

const isIssue = (value: unknown): value is ErrorIssue => {
  const issue = value as Partial<ErrorIssue> | null
  return (
    typeof issue === 'object' &&
    issue !== null &&
    typeof issue.code === 'string' &&
    Array.isArray(issue.path) &&
    issue.path.every((p) => typeof p === 'string' || typeof p === 'number')
  )
}

/**
 * The problems of a refused document (the course editor's), each at the path of its field and
 * with a code, never the value sent. None when the refusal is not about the document's fields.
 */
export function errorIssues(err: unknown): readonly ErrorIssue[] {
  const issues = data(err)?.issues
  return Array.isArray(issues) ? issues.filter(isIssue) : []
}

/** The server no longer knows the session (signed out elsewhere, expired or revoked). */
export function sessionGone(err: unknown): boolean {
  // "reauthenticate" asks an HR session to sign in again; the session itself still exists.
  return errorCode(err) === 'UNAUTHORIZED' && !needsReauthentication(err)
}

/** An HR operation refused because the sign-in is more than 12 hours old (PRD 01 §2.4). */
export function needsReauthentication(err: unknown): boolean {
  return errorReason(err) === 'reauthenticate'
}

/**
 * The HTTP status of a failed call: the one the API gives in its answer or, when the answer is
 * not the API's own (a body over the server's limit, which the server refuses before the API
 * reads it), the response's. Null when no answer came.
 */
export function errorStatus(err: unknown): number | null {
  const status = data(err)?.httpStatus
  if (typeof status === 'number') return status
  if (!(err instanceof TRPCClientError)) return null
  const response = (err.meta as { response?: { status?: unknown } } | undefined)?.response
  return typeof response?.status === 'number' ? response.status : null
}

/**
 * The server refused the call (a 4xx: not found, not allowed, invalid, sign in again): asking
 * again gives the same answer, so it is not retried.
 */
export function refused(err: unknown): boolean {
  const status = errorStatus(err)
  return status !== null && status >= 400 && status < 500
}
